
import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './ImageList.css';
import { getMarginFromProps, getPaddingFromProps, sanitizeAuthorInput, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { IMarginControl, IPaddingControl } from '../../types/shared/SpacingControl';
import { useWidthContext } from '../../providers/Width';
import { MD5 } from 'crypto-js';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { Row, Col, Title, Container } from '../../importCL';

interface IImage {
    image: string;
    imageAltText: string;
    imageCaption: string;
    imageLink: string;
    imageWidth: number;
}

interface IImageListProps extends MappedComponentProperties, IMarginControl, IPaddingControl {
    listTitle?: string;
    imageList?: IImage[];
    disableStackOnMobile?: boolean;
    desktopJustify?: 'left' | 'center' | 'right';
    mobileJustify?: 'left' | 'center' | 'right';
}

export const ImageListEditConfig = {
    emptyLabel: 'ImageList',
    isEmpty: (props: PropsWithChildren<IImageListProps>) => {
        return !props || !props.imageList || !props.imageList.length;
    },
};

const ImageList = (props: PropsWithChildren<IImageListProps>): JSX.Element => {
    const { width } = useWidthContext();

    const padding = getPaddingFromProps(props, width);
    const margin = getMarginFromProps(props, width);
    const componentHash = MD5(props.cqPath);

    return (
        <Row id='image-list' as='div' className='ImageList' style={{ ...padding, ...margin }}>
            <style type='text/css'>
                {
                    `.ImageList .list-items__item.Col.a${componentHash} {
                            width: ${width > 768 || props.disableStackOnMobile ? 'auto !important' : ''};
                    }`
                }
            </style>
            <Col as='div'  xs={12}>
                {props.listTitle && (
                    <Container>
                        <Title tag='h3' align='center' className='h4'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.listTitle))}</Title>
                    </Container>
                )}
                <Row as='div' className={`list-items list-items--${width <= 768 ? props.mobileJustify : props.desktopJustify} ${width <= 768 && !props.disableStackOnMobile ? 'list-items--stacked' : ''}`}>
                    {props.imageList?.map((image: IImage, index: number) => {
                        return (
                            <Col as='div' key={index} className={`list-items__item a${componentHash} ${props.disableStackOnMobile ? 'list-items__item--disableStack' : ''}`}>
                                <div className={`list-items__itemContainer ${width <= 768 && !props.disableStackOnMobile ? 'list-items__itemContainer--stacked' : ''} ${width <= 768 && !props.disableStackOnMobile && props.mobileJustify ? props.mobileJustify : ''}`}>
                                    {image.imageLink && (
                                        <LinkWrapper href={image.imageLink} target='_blank' rel='noopener'>
                                            <img loading='lazy' src={image.image} alt={image.imageAltText} style={{ width: image.imageWidth || 'auto' }} />
                                        </LinkWrapper>
                                    )}
                                    {!image.imageLink && (
                                        <img loading='lazy' src={image.image} alt={image.imageAltText} style={{ width: image.imageWidth || 'auto' }} />
                                    )}
                                    {image.imageCaption && (
                                        <p>{transformHTMLParserLinks(sanitizeAuthorInput(image.imageCaption))}</p>
                                    )}
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </Col>
        </Row>
    );
};

export default ImageList;
